import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { animalesAPI, exportacionAPI, reportesAPI } from '../services/api';
import { formatDate, calcularEdad, getCategoriaColor, getEstadoColor } from '../utils/helpers';
import RegistrarAnimalForm from '../components/Animales/RegistrarAnimalForm';
import { getCachedAnimales, getCacheStats } from '../services/offlineDB';

const AnimalesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [animales, setAnimales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegistroForm, setShowRegistroForm] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineInfo, setOfflineInfo] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null); // null | {type:'single',id,vid} | {type:'batch'}
  const [deleting, setDeleting] = useState(false);
  const [bulk, setBulk] = useState({ sexo: '', categoria: '' });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');

  const aplicarLote = async () => {
    if (!bulk.sexo && !bulk.categoria) return;
    setBulkSaving(true); setBulkMsg('');
    try {
      const res = await animalesAPI.updateBatch({
        animal_ids: Array.from(selected), establecimiento_id: user.establecimiento_id,
        sexo: bulk.sexo || undefined, categoria: bulk.categoria || undefined,
      });
      setBulkMsg(res.data.message);
      setSelected(new Set()); setBulk({ sexo: '', categoria: '' });
      await cargarAnimales();
    } catch (e) {
      setBulkMsg(e.response?.data?.detail || 'Error al actualizar');
    } finally { setBulkSaving(false); }
  };
  const [kpis, setKpis] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    sexo: '',
    categoria: '',
    estado: 'activo',
    buscar: '',
  });

  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  useEffect(() => {
    cargarAnimales();
    cargarKPIs();
  }, [pagination.page, filters, isOffline]);

  const cargarKPIs = async () => {
    if (!navigator.onLine || !user?.establecimiento_id) return;
    try {
      const res = await animalesAPI.kpisRodeo({
        establecimiento_id: user.establecimiento_id,
        ...filters,
      });
      setKpis(res.data);
    } catch { /* silent */ }
  };

  const cargarAnimales = async () => {
    setLoading(true);
    setSelected(new Set());
    if (navigator.onLine) {
      try {
        const response = await animalesAPI.getAll({
          establecimiento_id: user.establecimiento_id,
          page: pagination.page,
          page_size: pagination.pageSize,
          ...filters,
        });
        setAnimales(response.data.data);
        setPagination((prev) => ({
          ...prev,
          total: response.data.total,
          totalPages: response.data.total_pages,
        }));
        setOfflineInfo(null);
        setLoading(false);
        return;
      } catch (error) {
        console.warn('API failed, falling back to cache:', error.message);
      }
    }

    try {
      let cached = await getCachedAnimales(user.establecimiento_id);
      const stats = await getCacheStats();
      if (filters.sexo) cached = cached.filter(a => filters.sexo === 'sin_definir' ? !a.sexo : a.sexo === filters.sexo);
      if (filters.categoria) cached = cached.filter(a => filters.categoria === 'sin_definir' ? !a.categoria : a.categoria === filters.categoria);
      if (filters.estado) cached = cached.filter(a => a.estado === filters.estado);
      if (filters.buscar) {
        const q = filters.buscar.toLowerCase();
        cached = cached.filter(a =>
          (a.VID && a.VID.toLowerCase().includes(q)) ||
          (a.IDE && a.IDE.toLowerCase().includes(q)) ||
          (a.raza && a.raza.toLowerCase().includes(q))
        );
      }
      const total = cached.length;
      const start = (pagination.page - 1) * pagination.pageSize;
      const paginated = cached.slice(start, start + pagination.pageSize);
      setAnimales(paginated);
      setPagination(prev => ({ ...prev, total, totalPages: Math.ceil(total / prev.pageSize) }));
      setOfflineInfo({ count: stats.animalesCached, lastCache: stats.lastCacheTime, pendingOps: stats.pendingOps });
    } catch {
      setAnimales([]);
      setOfflineInfo({ count: 0, lastCache: null, pendingOps: 0 });
    }
    setLoading(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRegistroSuccess = () => {
    setShowRegistroForm(false);
    cargarAnimales();
    cargarKPIs();
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === animales.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(animales.map(a => a.id)));
    }
  };

  const handleDeleteSingle = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'single') return;
    setDeleting(true);
    try {
      await animalesAPI.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      cargarAnimales();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  const handleDeleteBatch = async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'batch') return;
    setDeleting(true);
    try {
      await animalesAPI.deleteBatch([...selected]);
      setDeleteConfirm(null);
      setSelected(new Set());
      cargarAnimales();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  if (showRegistroForm) {
    return (
      <div className="p-6">
        <RegistrarAnimalForm
          onSuccess={handleRegistroSuccess}
          onCancel={() => setShowRegistroForm(false)}
        />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Offline Banner */}
      {(isOffline || offlineInfo) && (
        <div className={`mb-4 px-4 py-3 rounded-lg flex items-center justify-between text-sm ${isOffline ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-blue-50 border border-blue-200 text-blue-800'}`} data-testid="offline-banner">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${isOffline ? 'bg-amber-500' : 'bg-blue-500'} animate-pulse`} />
            <span className="font-semibold">{isOffline ? 'MODO CAMPO' : 'Datos locales'}</span>
            {offlineInfo && (
              <span className="text-xs opacity-75">
                — {offlineInfo.count} animales en cache
                {offlineInfo.lastCache && ` (${new Date(offlineInfo.lastCache).toLocaleDateString()})`}
                {offlineInfo.pendingOps > 0 && ` — ${offlineInfo.pendingOps} operacion(es) pendiente(s)`}
              </span>
            )}
          </div>
          {isOffline && (
            <span className="px-2 py-1 bg-amber-200 rounded text-xs font-bold">SIN INTERNET</span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Animales</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Total: {pagination.total} animales registrados
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={async () => {
              try {
                const res = await exportacionAPI.animales(user.establecimiento_id, 'xlsx');
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'animales.xlsx'; a.click();
              } catch { alert('Error al exportar'); }
            }}
            className="px-4 py-2 border border-green-600 text-green-700 rounded-lg hover:bg-green-50 transition text-sm font-medium"
            data-testid="export-xlsx-btn"
          >
            Excel
          </button>
          <button
            onClick={async () => {
              try {
                const res = await exportacionAPI.animales(user.establecimiento_id, 'csv');
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'animales.csv'; a.click();
              } catch { alert('Error al exportar'); }
            }}
            className="px-4 py-2 border border-gray-400 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
            data-testid="export-csv-btn"
          >
            CSV
          </button>
          <button
            onClick={async () => {
              try {
                const res = await exportacionAPI.rodeoCompleto(user.establecimiento_id);
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'rodeo_completo.xlsx'; a.click();
              } catch { alert('Error al exportar'); }
            }}
            className="px-4 py-2 border border-blue-500 text-blue-700 rounded-lg hover:bg-blue-50 transition text-sm font-medium"
            data-testid="export-rodeo-btn"
          >
            Rodeo Completo
          </button>
          <button
            onClick={async () => {
              try {
                const res = await reportesAPI.rodeoPDF(user.establecimiento_id);
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'rodeo.pdf'; a.click();
              } catch { alert('Error al generar PDF'); }
            }}
            className="px-4 py-2 border border-red-400 text-red-700 rounded-lg hover:bg-red-50 transition text-sm font-medium"
            data-testid="export-pdf-btn"
          >
            PDF
          </button>
          <button
            onClick={() => setShowRegistroForm(true)}
            className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition"
            data-testid="registrar-animal-btn"
          >
            + Registrar Animal
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Buscar (VID/IDE)</label>
            <input
              type="text"
              value={filters.buscar}
              onChange={(e) => handleFilterChange('buscar', e.target.value)}
              placeholder="Buscar..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sexo</label>
            <select value={filters.sexo} onChange={(e) => handleFilterChange('sexo', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
              <option value="">Todos</option>
              <option value="hembra">Hembra</option>
              <option value="macho">Macho</option>
              <option value="sin_definir">Sin definir</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
            <select value={filters.categoria} onChange={(e) => handleFilterChange('categoria', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
              <option value="">Todas</option>
              <option value="ternero">Ternero</option>
              <option value="desmamante">Desmamante</option>
              <option value="vaquilla">Vaquilla</option>
              <option value="novillo">Novillo</option>
              <option value="vaca">Vaca</option>
              <option value="sin_definir">Sin definir</option>
              <option value="toro">Toro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select value={filters.estado} onChange={(e) => handleFilterChange('estado', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
              <option value="">Todos</option>
              <option value="activo">Activo</option>
              <option value="vendido">Vendido</option>
              <option value="descartado">Descartado</option>
              <option value="muerto">Muerto</option>
            </select>
          </div>
        </div>
      </div>

      {/* KPIs del Rodeo */}
      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="rodeo-kpis">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Animales</p>
                <p className="text-xl font-bold text-gray-900" data-testid="kpi-total-animales">{kpis.total_animales.toLocaleString()}</p>
                {kpis.animales_con_pesaje < kpis.total_animales && (
                  <p className="text-xs text-gray-400">{kpis.animales_con_pesaje} con pesaje</p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Peso Total Rodeo</p>
                <p className="text-xl font-bold text-blue-700" data-testid="kpi-peso-total">{kpis.peso_total.toLocaleString(undefined, {maximumFractionDigits: 0})} kg</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Peso Promedio</p>
                <p className="text-xl font-bold text-amber-700" data-testid="kpi-peso-promedio">{kpis.peso_promedio > 0 ? `${kpis.peso_promedio.toLocaleString(undefined, {maximumFractionDigits: 1})} kg` : 'N/A'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">GMD Promedio</p>
                <p className="text-xl font-bold text-purple-700" data-testid="kpi-gmd-promedio">{kpis.gmd_promedio != null ? `${kpis.gmd_promedio.toFixed(3)} kg/d` : 'N/A'}</p>
                {kpis.animales_con_gmd > 0 && (
                  <p className="text-xs text-gray-400">{kpis.animales_con_gmd} animales</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-amber-900">
            {selected.size} animal(es) seleccionado(s)
          </span>
          <div className="flex flex-wrap items-center gap-2" data-testid="bulk-edit-controls">
            <select value={bulk.sexo} onChange={(e) => setBulk({ ...bulk, sexo: e.target.value })} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white" data-testid="bulk-sexo-select">
              <option value="">Sexo: sin cambio</option>
              <option value="hembra">Hembra</option>
              <option value="macho">Macho</option>
            </select>
            <select value={bulk.categoria} onChange={(e) => setBulk({ ...bulk, categoria: e.target.value })} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white" data-testid="bulk-categoria-select">
              <option value="">Categoria: sin cambio</option>
              <option value="ternero">Ternero</option>
              <option value="desmamante">Desmamante</option>
              <option value="vaquilla">Vaquilla</option>
              <option value="novillo">Novillo</option>
              <option value="vaca">Vaca</option>
              <option value="toro">Toro</option>
            </select>
            <button
              onClick={aplicarLote}
              disabled={bulkSaving || (!bulk.sexo && !bulk.categoria)}
              className="px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition disabled:opacity-50"
              data-testid="bulk-apply-btn"
            >
              {bulkSaving ? 'Aplicando...' : 'Aplicar a seleccionados'}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelected(new Set())}
              className="text-sm text-gray-600 hover:text-gray-800"
              data-testid="clear-selection-btn"
            >
              Deseleccionar
            </button>
            <button
              onClick={() => setDeleteConfirm({ type: 'batch' })}
              className="px-4 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium transition"
              data-testid="delete-selected-btn"
            >
              Eliminar seleccionados
            </button>
          </div>
        </div>
      )}

      {bulkMsg && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg px-4 py-2" data-testid="bulk-msg">{bulkMsg}</div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando animales...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-center w-10">
                      <input
                        type="checkbox"
                        checked={animales.length > 0 && selected.size === animales.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IDE (RFID)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sexo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Edad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Raza</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {animales.map((animal) => (
                    <tr
                      key={animal.id}
                      className={`hover:bg-gray-50 cursor-pointer ${selected.has(animal.id) ? 'bg-red-50' : ''}`}
                      data-testid={`animal-row-${animal.VID}`}
                    >
                      <td className="px-3 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(animal.id)}
                          onChange={() => toggleSelect(animal.id)}
                          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                          data-testid={`select-animal-${animal.VID}`}
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" onClick={() => navigate(`/animales/${animal.id}`)}>
                        <div className="text-sm font-medium text-gray-900">{animal.VID}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" onClick={() => navigate(`/animales/${animal.id}`)}>
                        <div className="text-sm text-gray-500 font-mono">{animal.IDE || '-'}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" onClick={() => navigate(`/animales/${animal.id}`)}>
                        <span className="capitalize text-sm text-gray-900">{animal.sexo}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" onClick={() => navigate(`/animales/${animal.id}`)}>
                        {calcularEdad(animal.fecha_nacimiento)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" onClick={() => navigate(`/animales/${animal.id}`)}>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoriaColor(animal.categoria)}`}>
                          {animal.categoria?.replace('_', ' ') || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" onClick={() => navigate(`/animales/${animal.id}`)}>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getEstadoColor(animal.estado)}`}>
                          {animal.estado}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" onClick={() => navigate(`/animales/${animal.id}`)}>
                        {animal.raza}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setDeleteConfirm({ type: 'single', id: animal.id, vid: animal.VID })}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Eliminar animal"
                          data-testid={`delete-animal-${animal.VID}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginacion */}
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Anterior</button>
                <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages} className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Siguiente</button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{(pagination.page - 1) * pagination.pageSize + 1}</span> a{' '}
                    <span className="font-medium">{Math.min(pagination.page * pagination.pageSize, pagination.total)}</span> de{' '}
                    <span className="font-medium">{pagination.total}</span> resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Anterior</button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">Pagina {pagination.page} de {pagination.totalPages}</span>
                    <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Siguiente</button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Confirmar eliminacion</h3>
            </div>

            {deleteConfirm.type === 'single' ? (
              <p className="text-sm text-gray-600 mb-6">
                Estas por eliminar permanentemente el animal <strong>VID {deleteConfirm.vid}</strong> y todos sus registros asociados (pesajes, servicios, pariciones, controles).
                <br /><br />
                <span className="text-red-600 font-medium">Esta accion no se puede deshacer.</span>
              </p>
            ) : (
              <p className="text-sm text-gray-600 mb-6">
                Estas por eliminar permanentemente <strong>{selected.size} animal(es)</strong> y todos sus registros asociados (pesajes, servicios, pariciones, controles).
                <br /><br />
                <span className="text-red-600 font-medium">Esta accion no se puede deshacer.</span>
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition"
                data-testid="cancel-delete-btn"
              >
                Cancelar
              </button>
              <button
                onClick={deleteConfirm.type === 'single' ? handleDeleteSingle : handleDeleteBatch}
                disabled={deleting}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnimalesPage;
